import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/status";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PMPayments() {
  const { user, refreshUser } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/payouts").then((r) => setPayouts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const addCard = async () => {
    setBusy(true);
    try {
      await api.post("/stripe/payment-method", { card_last4: "4242", brand: "visa" });
      toast.success("Payment method added");
      await refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const total = payouts.filter((p) => p.status === "paid").reduce((s, p) => s + (p.gross_charge ?? p.amount), 0);
  const fees = payouts.filter((p) => p.status === "paid").reduce((s, p) => s + (p.host_fee || 0), 0);

  return (
    <div className="space-y-6" data-testid="pm-payments">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Payments</h1>
        <p className="text-muted-foreground text-sm mt-1">Your payment method and payout history.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="border border-border rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Payment Method</div>
          {user?.payment_method ? (
            <div className="flex items-center gap-3 mt-3">
              <div className="w-10 h-10 rounded-md bg-sapphire text-white flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
              <div>
                <div className="font-semibold text-sapphire capitalize">{user.payment_method.brand} •••• {user.payment_method.last4}</div>
                <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</div>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-sm text-muted-foreground mb-3">Add a card to fund payouts to cleaners.</p>
              <Button onClick={addCard} disabled={busy} className="bg-ocean hover:bg-ocean/90" data-testid="add-card">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" /> Add Test Card</>}
              </Button>
            </div>
          )}
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total Spent</div>
          <div className="font-heading font-black text-4xl text-sapphire mt-2">{money(total)}</div>
          <div className="text-sm text-muted-foreground mt-1">Incl. {money(fees)} service fees · {payouts.filter((p) => p.status === "paid").length} jobs</div>
        </div>
      </div>

      <div>
        <h2 className="font-heading font-bold text-xl text-sapphire mb-3">Payout History</h2>
        <div className="border border-border rounded-md bg-white divide-y divide-border">
          {payouts.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No payouts yet.</div>}
          {payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-5 py-4" data-testid={`payout-${p.id}`}>
              <div>
                <div className="font-semibold text-sapphire">Cleaning payout</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(p.created_at).toLocaleString()} · {money(p.base_amount ?? p.amount)} job + {money(p.host_fee || 0)} service fee · {p.mode}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-sapphire">{money(p.gross_charge ?? p.amount)}</div>
                <span className={`text-xs font-semibold ${p.status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
